/* eslint-disable react/no-danger */
import PropTypes from 'prop-types';
import uniqid from 'uniqid';
import { PopUpLarge } from '@bwoty-web/styleguide2/modal';
import { Picture } from '@bwoty-web/styleguide';
import RoomGallery from './roomGallery';
import ConceptInfo from './concept/conceptInfo';
import PartInfo from './concept/partInfo';
import SectionParagraph from '../../../../common/sectionParagraph';
import './roomTypePopup.scss';
import { getGalleryImage } from './roomTypeGallerySrc';

const RoomType = ({
  isConceptHotel,
  accommodationSection,
  toggleRoomInfo,
  roomType,
  imageDomain,
  generalUspList,
}) => {
  if (!roomType) {
    return null;
  }

  const galleryImages = isConceptHotel
    ? roomType?.images?.gallery
    : roomType?.images?.map((image) => {
        return getGalleryImage(image.src, imageDomain, image.alt);
      });

  const hasNoImage = galleryImages?.length < 1;

  if (hasNoImage) {
    const roomImageMissing = accommodationSection?.images?.find(
      (i) => i.itemKey === 'roomImageMissing',
    );

    galleryImages.push(
      getGalleryImage(
        roomImageMissing?.image?.mediaUrl,
        imageDomain,
        roomImageMissing?.alt,
      ),
    );
  }

  return (
    <PopUpLarge
      close={() => toggleRoomInfo()}
      defaultPadding
      className="gw-room-type-popup--scroll-anchor'"
      closeBottom
    >
      <div className="gw-room-type-popup">
        <RoomGallery 
          gallery={galleryImages} 
          hasNoImage={hasNoImage} 
          labels={accommodationSection?.labels}
        />
        <div className="gw-room-type-popup__left">
          <div className="gw-room-type-popup__heading-container">
            <h2 className="gw-room-type-popup__heading">{roomType?.name}</h2>
            {roomType?.part?.name && (
              <span className="gw-room-type-popup__building">
                {roomType?.part?.name}
              </span>
            )}
          </div>
          {isConceptHotel ? (
            <div
              className="gw-room-type-popup__description"
              dangerouslySetInnerHTML={{
                __html: roomType?.texts?.description || '',
              }}
            />
          ) : (
            roomType?.texts?.map((paragraph) => {
              return (
                <SectionParagraph
                  key={uniqid('room-type-paragraph')}
                  paragraph={paragraph}
                />
              );
            })
          )}
          {roomType?.images?.floorPlans && (
            <div className="gw-room-type-popup__floorplans">
              {(roomType?.images?.floorPlans || []).map((picture) => (
                <div key={uniqid('room-floorplans-picture-')}>
                  <Picture sources={picture?.sources} lazy />
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="gw-room-type-popup__right">
          {isConceptHotel ? (
            <ConceptInfo concept={roomType?.concept} />
          ) : (
            <SectionParagraph
              key={uniqid('room-type-general-usp-list')}
              paragraph={generalUspList}
            />
          )}
        </div>
        {isConceptHotel && (
          <PartInfo
            roomType={roomType}
            texts={accommodationSection.labels}
            toggleRoomInfo={toggleRoomInfo}
          />
        )}
      </div>
    </PopUpLarge>
  );
};

RoomType.propTypes = {
  isConceptHotel: PropTypes.bool.isRequired,
  imageDomain: PropTypes.string.isRequired,
  accommodationSection: PropTypes.shape({
    labels: PropTypes.shape(),
    images: PropTypes.arrayOf(
      PropTypes.shape({
        roomImageMissing: PropTypes.shape(),
      }),
    ),
  }).isRequired,
  toggleRoomInfo: PropTypes.func.isRequired,
  generalUspList: PropTypes.shape({}),
  roomType: PropTypes.shape({
    meta: PropTypes.shape({
      id: PropTypes.number,
    }),
    name: PropTypes.string,
    texts: PropTypes.oneOfType([
      PropTypes.arrayOf(PropTypes.shape({})),
      PropTypes.shape({
        description: PropTypes.string,
      }),
    ]),
    concept: PropTypes.shape({
      name: PropTypes.string,
      texts: PropTypes.shape({
        description: PropTypes.string,
      }),
      usps: PropTypes.shape({
        items: PropTypes.arrayOf(PropTypes.string),
      }),
    }),
    part: PropTypes.shape({
      name: PropTypes.string,
      floorPlan: PropTypes.shape({
        images: PropTypes.arrayOf(
          PropTypes.shape({
            sources: PropTypes.arrayOf(
              PropTypes.shape({
                srcSet: PropTypes.string,
                type: PropTypes.string,
                media: PropTypes.string,
              }),
            ),
            fallback: PropTypes.shape({
              alt: PropTypes.string,
              role: PropTypes.string,
              src: PropTypes.string,
              srcSet: PropTypes.string,
            }),
            text: PropTypes.string,
          }),
        ),
        roomsShort: PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.number,
            name: PropTypes.string,
            image: PropTypes.shape({
              src: PropTypes.string,
            }),
          }),
        ),
      }),
    }),
    images: PropTypes.oneOfType([
      PropTypes.shape({
        floorPlans: PropTypes.arrayOf(
          PropTypes.shape({
            sources: PropTypes.arrayOf(
              PropTypes.shape({
                srcSet: PropTypes.string,
                type: PropTypes.string,
                media: PropTypes.string,
              }),
            ),
            fallback: PropTypes.shape({
              alt: PropTypes.string,
              role: PropTypes.string,
              src: PropTypes.string,
              srcSet: PropTypes.string,
            }),
            text: PropTypes.string,
          }),
        ),
        gallery: PropTypes.arrayOf(
          PropTypes.shape({
            sources: PropTypes.arrayOf(
              PropTypes.shape({
                srcSet: PropTypes.string,
                type: PropTypes.string,
                media: PropTypes.string,
              }),
            ),
            fallback: PropTypes.shape({
              alt: PropTypes.string,
              role: PropTypes.string,
              src: PropTypes.string,
              srcSet: PropTypes.string,
            }),
            text: PropTypes.string,
          }),
        ),
      }),
      PropTypes.arrayOf(
        PropTypes.shape({
          src: PropTypes.string,
          alt: PropTypes.string,
        }),
      ),
    ]),
  }),
};

RoomType.defaultProps = {
  roomType: null,
  generalUspList: null,
};

export default RoomType;
